import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    console.log("Task selection controller connected")
    this.selectedStories = new Set()
    this.loadSelectedStories()
  }
  
  loadSelectedStories() {
    const checkboxes = this.element.querySelectorAll('input[type="checkbox"]:checked')
    checkboxes.forEach(cb => {
      this.selectedStories.add(cb.value)
    })
  }
  
  toggleStory(event) {
    const storyId = event.target.value
    
    if (event.target.checked) {
      this.selectedStories.add(storyId)
    } else {
      this.selectedStories.delete(storyId)
    }
    
    this.broadcastSelection()
  }
  
  selectStory(event) {
    // Don't select if clicking on checkbox or button
    if (event.target.closest('input, button, a')) return
    
    const row = event.currentTarget
    const storyId = row.dataset.storyId
    
    // Highlight the selected row
    this.element.querySelectorAll('tr[data-story-id]').forEach(r => {
      r.classList.remove('bg-primary/10')
    })
    row.classList.add('bg-primary/10')
    
    // Broadcast story selection
    this.broadcastStorySelection(storyId)
  }
  
  preventPropagation(event) {
    event.stopPropagation()
  }
  
  showDetails(event) {
    event.stopPropagation()
    const row = event.target.closest('tr')
    const storyId = row.dataset.storyId
    
    // Dispatch event for showing story details
    const detailsEvent = new CustomEvent('story:show-details', {
      detail: { storyId },
      bubbles: true
    })
    this.element.dispatchEvent(detailsEvent)
  }
  
  async startStory(event) {
    event.stopPropagation()
    const storyId = event.currentTarget.dataset.storyId
    const buildSessionInput = this.element.querySelector('[data-build-session-id]')
    
    if (!buildSessionInput) {
      console.error('Build session ID not found')
      return
    }
    
    const buildSessionId = buildSessionInput.dataset.buildSessionId
    // Get the current path from the URL which already has the app_project slug
    const pathParts = window.location.pathname.split('/')
    const appProjectSlug = pathParts[2] // /app_projects/{slug}/build_sessions/{id}
    
    console.log('Starting story:', storyId, 'for build session:', buildSessionId)
    
    const response = await fetch(`/app_projects/${appProjectSlug}/build_sessions/${buildSessionId}/start_story`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ story_id: storyId })
    })
    
    if (response.ok) {
      window.location.reload()
    } else {
      console.error('Failed to start story')
    }
  }
  
  async markAsDone(event) {
    event.stopPropagation()
    const storyId = event.currentTarget.dataset.storyId
    const buildSessionInput = this.element.querySelector('[data-build-session-id]')
    
    if (!buildSessionInput) {
      console.error('Build session ID not found')
      return
    }
    
    const buildSessionId = buildSessionInput.dataset.buildSessionId
    // Get the current path from the URL which already has the app_project slug
    const pathParts = window.location.pathname.split('/')
    const appProjectSlug = pathParts[2] // /app_projects/{slug}/build_sessions/{id}
    
    // Update UI optimistically
    const button = event.currentTarget
    const row = button.closest('tr')
    const statusBadge = row.querySelector('td:nth-child(3) span') // Status column
    
    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Marking...'
    
    console.log('Marking story as done:', storyId, 'for build session:', buildSessionId)
    
    const response = await fetch(`/app_projects/${appProjectSlug}/build_sessions/${buildSessionId}/mark_story_done`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html, application/json'
      },
      body: JSON.stringify({ story_id: storyId })
    })
    
    if (response.ok) {
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('text/vnd.turbo-stream.html')) {
        // Turbo Stream response - it will handle the UI update
        const text = await response.text()
        Turbo.renderStreamMessage(text)
      } else {
        // JSON response - update UI manually
        if (statusBadge) {
          statusBadge.className = 'badge badge-sm badge-success'
          statusBadge.textContent = 'Completed'
        }
        
        // Remove or update the action button
        const dropdown = button.closest('.dropdown-content')
        if (dropdown) {
          button.closest('li').remove()
        }
        
        // Optionally fade out the row after a delay
        setTimeout(() => {
          row.style.transition = 'opacity 0.5s'
          row.style.opacity = '0.5'
        }, 500)
      }
    } else {
      console.error('Failed to mark story as done:', response.status)
      // Restore button on error
      button.disabled = false
      button.innerHTML = '<i class="fas fa-check-circle text-primary"></i> Mark as Done'
    }
  }
  
  async removeFromQueue(event) {
    event.stopPropagation()
    const storyId = event.currentTarget.dataset.storyId
    const buildSessionInput = this.element.querySelector('[data-build-session-id]')
    
    if (!buildSessionInput || !confirm('Remove this story from the queue?')) return
    
    const buildSessionId = buildSessionInput.dataset.buildSessionId
    // Get the current path from the URL which already has the app_project slug
    const pathParts = window.location.pathname.split('/')
    const appProjectSlug = pathParts[2] // /app_projects/{slug}/build_sessions/{id}
    
    const button = event.currentTarget
    const row = button.closest('tr')
    
    // Show loading state
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'
    
    console.log('Removing story from queue:', storyId, 'for build session:', buildSessionId)
    
    const response = await fetch(`/app_projects/${appProjectSlug}/build_sessions/${buildSessionId}/remove_from_queue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html, application/json'
      },
      body: JSON.stringify({ story_id: storyId })
    })
    
    if (response.ok) {
      // Animate row removal
      row.style.transition = 'all 0.3s ease-out'
      row.style.opacity = '0'
      row.style.transform = 'translateX(-100%)'
      
      setTimeout(() => {
        row.remove()
        // Check if queue is now empty
        const remainingRows = this.element.querySelectorAll('tbody tr')
        if (remainingRows.length === 0) {
          // Show empty state
          const tableContainer = this.element.querySelector('.phase-group')
          if (tableContainer) {
            tableContainer.innerHTML = `
              <div class="text-center py-12">
                <i class="fas fa-inbox text-5xl text-base-content/20 mb-4"></i>
                <p class="text-base-content/60 mb-4">No stories in queue</p>
                <button class="btn btn-primary btn-sm" onclick="openStorySelectionModal()">
                  <i class="fas fa-plus-circle mr-1"></i>
                  Add Stories to Queue
                </button>
              </div>
            `
          }
        }
      }, 300)
    } else {
      console.error('Failed to remove story from queue')
      button.disabled = false
      button.innerHTML = '<i class="fas fa-times"></i>'
    }
  }
  
  broadcastSelection() {
    const event = new CustomEvent('story:selection-changed', {
      detail: { selectedIds: Array.from(this.selectedStories) },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
  
  broadcastStorySelection(storyId) {
    const event = new CustomEvent('story:selected', {
      detail: { storyId },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }
}